import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppContainer from "../../../components/AppContainer";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import ChangePasswordDialog from "../../../components/Dialog/ChangePasswordDialog";
import ProfileSaveDialog from "../../../components/Dialog/ProfileSaveDialog";
import RomanianPhoneInput from "../../../components/romaniaPhoneInput";
import UploadImageAvatar from "../../../components/UploadImage";
import globalStyle from "../../../globalStyle";
import { setUserData } from "../../../lib/slices/userSlice";
import { baseUrl } from "../../../services";
import {
  changePasswordCreator,
  creatorUpdateProfile,
} from "../../../services/creator/creatorApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { updateCreatorProfileValidation } from "../../../utils/Validation";
import Label from "./../../../components/Label/index";
import TextInput from "./../../../components/TextInputs/index";

// Same list as creator registration — baad mein API se aayenge
const INFLUENCER_TYPES = [
  { value: "food", label: "Food & Cooking" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "fitness", label: "Fitness & Health" },
  { value: "family", label: "Family & Parenting" },
  { value: "other", label: "Other" },
];

const socialFields = [
  {
    name: "facebook",
    label: "Facebook",
    placeholder: "https://facebook.com/yourpage",
    icon: <FacebookIcon sx={{ color: "#1877F2" }} />,
  },
  {
    name: "tiktok",
    label: "TikTok",
    placeholder: "https://tiktok.com/@yourname",
    icon: <MusicNoteIcon sx={{ color: "#000000" }} />,
  },
  {
    name: "instagram",
    label: "Instagram",
    placeholder: "https://instagram.com/yourname",
    icon: <InstagramIcon sx={{ color: "#E4405F" }} />,
  },
];

function Profile() {
  // changepassword dialog
  const openCpDialog = useRef(null);
  // profile save dialog
  const opensPsDialog = useRef(null);

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [inputValues, setInputValues] = useState({
    image: "",
    influencerType: "",
    name: "",
    email: "",
    phoneNumber: "",
    facebook: "",
    tiktok: "",
    instagram: "",
  });

  const [inputError, setInputError] = useState({
    influencerType: false,
    name: false,
    phoneNumber: false,
    socialRequired: false,
    facebook: false,
    tiktok: false,
    instagram: false,
  });

  const userData = useSelector((state) => state.userData.user);

  useEffect(() => {
    if (userData) {
      setInputValues({
        image: userData?.profileImage || "",
        influencerType: userData?.creatorType || "",
        name: userData?.username || "",
        email: userData?.email || "",
        phoneNumber: userData?.phoneNumber || "",
        facebook: userData?.socialLinks?.facebook || "",
        tiktok: userData?.socialLinks?.tiktok || "",
        instagram: userData?.socialLinks?.instagram || "",
      });
    }
  }, [userData]);

  // Render top right part on template
  const renderRightPart = () => {
    return (
      <Stack
        gap={2}
        direction={"row"}
        alignItems={"center"}
        flex={1}
        justifyContent={"flex-end"}
      >
        <Box height={40}>
          <GlobalButton
            variant={"outlined"}
            btnText={"resetPassword"}
            styleProps={{ width: "auto" }}
            handleClickBtn={() => openCpDialog.current.openDialog()}
          />
        </Box>
        <Box width={100} height={40}>
          <GlobalButton
            variant={"contained"}
            btnText={"save"}
            styleProps={{ fontWeight: 400 }}
            handleClickBtn={() => {
              const validate = updateCreatorProfileValidation(
                inputValues,
                setInputError,
              );
              if (!validate) {
                opensPsDialog.current.openDialog();
              }
            }}
          />
        </Box>
      </Stack>
    );
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", file);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;

    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      socialRequired: ["facebook", "tiktok", "instagram"].includes(name)
        ? false
        : prevError.socialRequired,
    }));
    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async (password) => {
    const payload = {
      profileImage: inputValues.image,
      username: inputValues.name,
      phoneNumber: inputValues.phoneNumber,
      creatorType: inputValues.influencerType,
      socialLinks: {
        facebook: inputValues.facebook,
        tiktok: inputValues.tiktok,
        instagram: inputValues.instagram,
      },
      password,
      authProvider: "local",
    };

    try {
      setIsLoading(true);
      const response = await creatorUpdateProfile(userData?._id, payload);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user-data", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        handleOpenSnackBar("success", response.data.message);
        opensPsDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (data) => {
    try {
      const payload = {
        userId: userData._id,
        newPassword: data.newPassword,
        oldPassword: data.oldPassword,
        role: "Creator",
      };
      setIsLoading(true);
      const response = await changePasswordCreator(payload);
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        openCpDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <AppContainer
        primaryHeading={t("personalDetails")}
        secondaryHeading={t("accountDetailsInfo")}
        headProps={renderRightPart()}
      >
        <Typography
          variant="h1"
          fontWeight={600}
          fontSize={"1.125rem"}
          mb={3}
          textTransform={"capitalize"}
        >
          {t("personalInformation")}
        </Typography>

        <Grid container mt={5}>
          <Grid item xs={12} md={3}>
            <UploadImageAvatar
              error={false}
              image={inputValues.image}
              handleImageChange={handleImageChange}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Label labelText={t("name")} required htmlFor={"name"} />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"name"}
                  required
                  inputError={inputError.name}
                  helperText={inputError.name && t("nameRequired")}
                  placeholder={`${t("enter")} ${t("name")}`}
                  name={"name"}
                  value={inputValues.name}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("emailAddress")}
                  required
                  htmlFor={"email"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"email"}
                  placeholder={`${t("enter")} ${t("emailAddress")}`}
                  required
                  readonly
                  name={"email"}
                  value={inputValues.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("phoneNumber")}
                  required
                  htmlFor={"phoneNumber"}
                />
                <RomanianPhoneInput
                  inputError={inputError.phoneNumber}
                  value={inputValues.phoneNumber}
                  onChange={(value) => {
                    setInputValues((prev) => ({ ...prev, phoneNumber: value }));
                    setInputError((err) => ({ ...err, phoneNumber: false }));
                  }}
                />
                {inputError.phoneNumber && (
                  <FormHelperText sx={{ color: "custom.errorRed" }}>
                    {t("phoneRequired", "Valid phone number is required")}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("influencerType", "Influencer Type")}
                  htmlFor="influencerType"
                  required
                />
                <FormControl fullWidth error={inputError.influencerType}>
                  <Select
                    id="influencerType"
                    name="influencerType"
                    value={inputValues.influencerType}
                    onChange={handleChangeInputValues}
                    displayEmpty
                    size="small"
                  >
                    <MenuItem value="" disabled>
                      {t("selectInfluencerType", "Select influencer type")}
                    </MenuItem>
                    {INFLUENCER_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {inputError.influencerType && (
                    <FormHelperText sx={{ color: "custom.errorRed" }}>
                      {t(
                        "influencerTypeRequired",
                        "Influencer type is required",
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography
          variant="h1"
          fontWeight={600}
          fontSize={"1.125rem"}
          mt={4}
        >
          {t("socialMediaLinks", "Social Media Links")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("atLeastOneSocial", "Add at least one profile link")}
        </Typography>
        {inputError.socialRequired && (
          <FormHelperText sx={{ color: "custom.errorRed" }}>
            {t("socialRequired", "Please add at least one social media link")}
          </FormHelperText>
        )}

        <Grid container mt={1} spacing={2}>
          {socialFields.map((field) => (
            <Grid item xs={12} md={6} key={field.name}>
              <Label labelText={field.label} htmlFor={field.name} />
              <TextInput
                id={field.name}
                inputError={inputError[field.name] || inputError.socialRequired}
                helperText={
                  inputError[field.name] &&
                  t("invalidSocialLink", `Enter a valid ${field.label} link`)
                }
                placeholder={field.placeholder}
                type="url"
                name={field.name}
                value={inputValues[field.name]}
                onChange={handleChangeInputValues}
                InputEndIcon={field.icon}
              />
            </Grid>
          ))}
        </Grid>
      </AppContainer>

      <ProfileSaveDialog
        ref={opensPsDialog}
        handleSaveProfile={handleSaveProfile}
      />

      <ChangePasswordDialog
        ref={openCpDialog}
        handleChangePassword={handleChangePassword}
      />
    </>
  );
}

export default Profile;
